import React from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from '@federico.moretti/react-translate';
import { Label } from '../../typography';
import { useTheme } from '../../theme';
import {
  RiskBarWrapper,
  Bar,
  Marker,
  LabelContainer,
  Segment as SegmentWrapper,
  InfoBox,
} from './styles';
import { SvgChevron } from '../../svg';
import { toPercentageString } from '../../utils/strings';

const Segment = React.memo((props) => {
  const { type, min, max, value, hideMarker, children } = props;
  const diff = max - min;
  let percent = Math.round(((value - min) * 100) / diff);
  if (!value && type === 'selected') percent = 50;
  const isMarkerVisible = value >= min && value < max;

  return (
    <SegmentWrapper type={type}>
      {isMarkerVisible && !hideMarker && (
        <>
          <Marker value={percent}>
            <span />
          </Marker>
        </>
      )}
      {children}
    </SegmentWrapper>
  );
});

Segment.propTypes = {
  type: PropTypes.oneOf(['below', 'above', 'selected']),
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.number,
  hideMarker: PropTypes.bool,
  children: PropTypes.node,
};

function RiskBar(props) {
  const { level, steps, value, style, stepLabels, hideMarker, hideLevel, levelInfo } = props;
  const { language } = useTranslate();
  const [updatedSteps, setUpdatedSteps] = React.useState(steps);
  const absValue = Math.abs(value);
  const theme = useTheme();

  React.useEffect(() => {
    const newSteps = [0, ...steps].sort();
    setUpdatedSteps(newSteps);
  }, [steps]);

  const [showInfo, setShowInfo] = React.useState(false);

  return (
    <RiskBarWrapper style={style}>
      <Bar>
        {[...Array(steps.length)].map((_, i) => (
          <Segment
            hideMarker={hideMarker}
            key={i}
            type={i + 1 < level ? 'below' : i + 1 === level ? 'selected' : 'above'}
            value={absValue}
            min={updatedSteps[i]}
            max={updatedSteps[i + 1]}
          >
            {levelInfo && i + 1 === level && (
              <InfoBox
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
              >
                {showInfo ? (
                  <>
                    {levelInfo.map((info) => (
                      <div key={info.label}>
                        <Label noMargin small>
                          {info.label}: {toPercentageString(info.value, language)}
                        </Label>
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <SvgChevron direction="top" stroke={theme.colors.secondary60w} />
                  </div>
                )}
              </InfoBox>
            )}
          </Segment>
        ))}
      </Bar>
      <LabelContainer>
        {[...Array(steps.length)].map((_, i) => (
          <Label key={i} small noMargin>
            {hideLevel ? stepLabels[i] : `${i + 1}${stepLabels ? ` – ${stepLabels[i]}` : ''}`}
          </Label>
        ))}
      </LabelContainer>
    </RiskBarWrapper>
  );
}

RiskBar.propTypes = {
  level: PropTypes.number.isRequired,
  value: PropTypes.number,
  steps: PropTypes.array.isRequired,
  stepLabels: PropTypes.array,
  style: PropTypes.object,
  levelInfo: PropTypes.array,
  hideMarker: PropTypes.bool,
  hideLevel: PropTypes.bool,
};

export default RiskBar;
